// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation — the PER-CATEGORY form definitions.
//
// This file is the single source of truth for "which form, which fields, which
// popup, for which category". Everything here is transcribed from the legacy
// Model.xafml DetailView LAYOUTS (not from the spec's prose tables, and not from
// the PropertyEditor lists — a PropertyEditor with no matching LayoutItem is
// NOT displayed, which is how Calibration ends up with no Solvent/SolventID
// even though both editors are declared).
//
//   ReagentPreparation_DetailView_Chemistry   (Model.xafml :37764)
//   ReagentPreparation_DetailView_MicroMedia  (Model.xafml :37816)
//   ReagentPreparation_DetailView_Calibration (Model.xafml :37694)
//
// RULE: nothing may appear here that XAF does not render, and every field XAF
// renders must appear, in XAF's column and XAF's order.
//
// CAPTIONS: a PropertyEditor with no Caption, on a member with no
// XafDisplayName, shows XAF's AUTO caption — the member name split at word
// boundaries ("ExpirationDate" → "Expiration Date", "SolventID" → "Solvent ID").
// Re-verified against Model.xafml 2026-10-16: there is no BOModel caption
// override and no shared ModelDifference row for these views, so the split
// name IS what the analyst reads. The earlier unsplit captions were wrong.
// ─────────────────────────────────────────────────────────────────────────────

/** Legacy `PrepSelectTypes`, declaration order — the value stored in
 *  dbo.ReagentPreparation.PrepSelectType. */
export const CHEMICAL = 0;
export const CALIBRATION = 1;
export const MICRO = 2;

/** Which control a field renders as. One entry per control type XAF actually
 *  uses on these three forms — no more. */
export type PrepFieldKind =
  | 'text'          // plain string
  | 'readonly'      // AllowEdit="False"
  | 'date'          // date picker
  | 'datePast'      // date picker, future dates disabled (spec §18 msg 3)
  | 'number'
  | 'textarea'
  | 'lookup'        // plain FK dropdown (<select>)
  | 'grid'          // ASPxGridLookup — multi-column popup (Vendor Stock, LT#, …)
  | 'enum'          // ASPxEnumPropertyEditor (Solvent)
  | 'radio'         // EnumRadioButtonListPropertyEditor (SelectStockSolution)
  | 'checkbox'      // BoolSolvent — read-only, caption hidden
  | 'levels'        // NoOfLevels + the inline [Ok] ActionContainerViewItem
  | 'tests';        // Test: XAF's CHECKED grid-lookup (multi-select, ctrl :4321)

export interface PrepField {
  /** Key on the ReagentPrepDetail / ReagentPrepInput record. */
  key: string;
  /** Caption as XAF shows it. Empty string = caption hidden (BoolSolvent). */
  label: string;
  kind: PrepFieldKind;
  /** Legacy [RuleRequiredField] — drives the red * and the yellow tint. */
  required?: boolean;
  /** Feed name. For 'lookup' one of the reader's simple whitelist; for
   *  'grid' one of the four grid-lookups. */
  lookup?: string;
  /** 'grid' only: the popup column captions, from the XAF LookupListView. */
  cols?: string[];
  /** 'grid' only: the field whose value narrows this lookup (LT# follows
   *  Vendor Stock, LLT# follows Lab Stock, Solvent LT# follows Solvent). */
  parentKey?: string;
  /** 'grid' only: the sibling holding the chosen row's display text. */
  textKey?: string;
  /** Rows for a textarea (XAF RowCount). */
  rows?: number;
  /** Render only when this predicate holds — used ONLY for the Calibration
   *  Vendor-Stock / Lab-Stock pair, which XAF shows/hides on the radio
   *  (spec §12.1: "shown when Vendor Stock is selected"). */
  showWhen?: 'vendorStock' | 'labStock';
}

export interface PrepFormDef {
  /** DetailView caption as XAF sets it. */
  caption: string;
  /** Left / right columns of the "Basic Information" group, in XAF order. */
  col1: PrepField[];
  col2: PrepField[];
  /** Caption of the child-grid group. null = ShowCaption="False" in XAF. */
  childCaption: string | null;
  /** XAF puts the Prep Notepad action on Chemistry + Micro Media only
   *  (PrepNotepad.TargetViewId, ctrl :69). */
  hasPrepNotepad: boolean;
  /** XAF puts LevelOfOk on Calibration only, and as an ActionContainerViewItem
   *  INSIDE the layout beside NoOfLevels — never in the toolbar. */
  hasLevels: boolean;
}

/** Chemistry — Model.xafml :37786-37803. Child group caption "Components". */
const CHEMISTRY: PrepFormDef = {
  caption: 'Reagent Prep Log',
  col1: [
    { key: 'prepNameId',     label: 'Prep Name',       kind: 'lookup', lookup: 'prep-names', required: true },
    { key: 'prepTypeId',     label: 'Prep Type',       kind: 'lookup', lookup: 'prep-types', required: true },
    { key: 'testIds',        label: 'Test',           kind: 'tests',  lookup: 'tests', cols: ['Matrix', 'Test Name', 'Method Number'] },
    { key: 'expirationDate', label: 'Expiration Date', kind: 'date',   required: true },
    { key: 'preparedByName', label: 'Prepared By',     kind: 'readonly' },
    { key: 'datePrepared',   label: 'Date Prepared',   kind: 'datePast' },
    { key: 'storageId',      label: 'Storage',        kind: 'lookup', lookup: 'storages', required: true },
  ],
  col2: [
    { key: 'solvent',     label: 'Solvent',   kind: 'enum' },
    // Caption="" + AllowEdit="False" in XAF — read-only, no caption.
    { key: 'boolSolvent', label: '',          kind: 'checkbox' },
    { key: 'solventID',   label: 'Solvent ID', kind: 'text' },
    { key: 'llt',         label: 'LLT',       kind: 'readonly' },
    { key: 'comment',     label: 'Comment',   kind: 'textarea', rows: 4 },
  ],
  childCaption: 'Components',
  hasPrepNotepad: true,
  hasLevels: false,
};

/** Micro Media — Model.xafml :37841-37857. Note Comment sits in COLUMN 1 here
 *  and LLT closes column 2, the reverse of Chemistry. Child group has
 *  ShowCaption="False", so no caption. */
const MICRO_MEDIA: PrepFormDef = {
  caption: 'Reagent Prep Log',
  col1: [
    { key: 'prepNameId',     label: 'Prep Name',       kind: 'lookup', lookup: 'prep-names', required: true },
    { key: 'prepTypeId',     label: 'Prep Type',       kind: 'lookup', lookup: 'prep-types', required: true },
    { key: 'testIds',        label: 'Test',           kind: 'tests',  lookup: 'tests', cols: ['Matrix', 'Test Name', 'Method Number'] },
    { key: 'storageId',      label: 'Storage',        kind: 'lookup', lookup: 'storages', required: true },
    { key: 'expirationDate', label: 'Expiration Date', kind: 'date',   required: true },
    { key: 'comment',        label: 'Comment',        kind: 'textarea', rows: 4 },
  ],
  col2: [
    { key: 'solvent',        label: 'Solvent',     kind: 'enum' },
    { key: 'boolSolvent',    label: '',            kind: 'checkbox' },
    { key: 'solventID',      label: 'Solvent ID',   kind: 'text' },
    { key: 'preparedByName', label: 'Prepared By',  kind: 'readonly' },
    { key: 'datePrepared',   label: 'Date Prepared', kind: 'datePast' },
    { key: 'llt',            label: 'LLT',         kind: 'readonly' },
  ],
  childCaption: null,
  hasPrepNotepad: true,
  hasLevels: false,
};

/** Calibration — Model.xafml :37724-37752.
 *
 *  ⚠️ NO Test, NO PrepType field (it auto-fills from PrepName, §12.1 header
 *  rules), and NO enum Solvent / BoolSolvent / SolventID: those PropertyEditors
 *  are declared but have no LayoutItem, so XAF does not render them. This
 *  form's "Solvent" is the CalibrationSolvent Items lookup, and "Solvent LT#"
 *  is CalibrationSolventLT — different fields entirely from the other two forms.
 *
 *  Vendor Stock / LT# and Lab Stock / LLT# are a mutually exclusive PAIR driven
 *  by the SelectStockSolution radio. */
const CALIBRATION_FORM: PrepFormDef = {
  caption: 'Calibration',
  col1: [
    { key: 'llt',                 label: 'LLT',                  kind: 'readonly' },
    { key: 'selectStockSolution', label: 'Select Stock Solution', kind: 'radio' },
    { key: 'stockConc',           label: 'Stock Conc',           kind: 'number' },
    { key: 'prepNameId',          label: 'Prep Name',             kind: 'lookup', lookup: 'prep-names', required: true },
    { key: 'vendorStockId',  label: 'Vendor Stock',   kind: 'grid', lookup: 'vendor-stock', cols: ['Item','Vendor','Description','Catalog#'], textKey: 'vendorStockName', showWhen: 'vendorStock' },
    { key: 'ltId',           label: 'Stock Solution', kind: 'grid', lookup: 'lt', cols: ['LT#','VendorLot','ExpiryDate'], textKey: 'ltText', parentKey: 'vendorStockId', showWhen: 'vendorStock' },
    { key: 'labStockId',     label: 'Lab Stock',      kind: 'grid', lookup: 'lab-stock', cols: ['PrepName','PrepType'], textKey: 'labStockName', showWhen: 'labStock' },
    { key: 'lltLabStockId',  label: 'Stock Solution', kind: 'grid', lookup: 'llt', cols: ['LLT','PrepType','PrepName','PrepDate','ExpiryDate'], textKey: 'lltLabStockText', parentKey: 'labStockId', showWhen: 'labStock' },
    { key: 'stockConcUnitId',     label: 'Stock Conc Unit',      kind: 'lookup', lookup: 'units' },
    { key: 'expirationDate',      label: 'Expiration Date',       kind: 'date',   required: true },
  ],
  col2: [
    { key: 'calibrationSolventId',   label: 'Solvent',     kind: 'grid', lookup: 'vendor-stock', cols: ['Item','Vendor','Description','Catalog#'], textKey: 'calibrationSolventName' },
    // No expiry filter on this one in legacy (BackendLogic §0.4), but it shares
    // the same Distribution feed; it narrows to the chosen solvent item.
    { key: 'calibrationSolventLtId', label: 'Solvent LT#', kind: 'grid', lookup: 'lt', cols: ['LT#','VendorLot','ExpiryDate'], textKey: 'calibrationSolventLtText', parentKey: 'calibrationSolventId' },
    // NoOfLevels + the inline [Ok] ActionContainerViewItem, one layout row.
    { key: 'noOfLevels',             label: 'No Of Levels', kind: 'levels' },
    { key: 'departmentName',         label: 'Department',   kind: 'readonly' },
    { key: 'storageId',              label: 'Storage',      kind: 'lookup', lookup: 'storages', required: true },
    { key: 'datePrepared',           label: 'Date Prepared', kind: 'datePast' },
    { key: 'preparedByName',         label: 'Prepared By',   kind: 'readonly' },
    { key: 'comment',                label: 'Comment',      kind: 'textarea', rows: 4 },
  ],
  childCaption: null,
  hasPrepNotepad: false,
  hasLevels: true,
};

/** The form for a category. Mirrors EditAction_Executing (:767-790), which
 *  switches on PrepSelectType to pick which DetailView to create. */
export function prepForm(prepSelectType: number): PrepFormDef {
  switch (prepSelectType) {
    case CALIBRATION: return CALIBRATION_FORM;
    case MICRO:       return MICRO_MEDIA;
    default:          return CHEMISTRY;
  }
}

/**
 * WHAT THE CLASSIFY POPUP'S "New Prep" TAB DOES PER CATEGORY (spec §8 tab 1,
 * ctrl Dc_Accepting) — the routing the whole module hangs off:
 *
 *   Chemical Reagent Prep     → open the **Prep Notepad (Chemistry)** popup
 *                               with no parent yet; its OK creates the header
 *                               and opens the Chemistry form in Edit.
 *   Micro Media and Reagent   → open the **Prep Notepad (Micro Media)** popup;
 *                               same flow.
 *   Calibration Set Prep      → NO popup. Create the header directly
 *                               (SelectStockSolution = VendorStock,
 *                               PrepType = "Calibration") and open the
 *                               Calibration form in Edit in the MAIN window.
 *
 * So Calibration is the only category that goes straight to a form, and it is
 * the only one with no Prep Notepad at all.
 */
export function opensPrepNotepadOnNew(prepSelectType: number): boolean {
  return prepSelectType !== CALIBRATION;
}
